import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faShield, faExclamationTriangle } from '@fortawesome/free-solid-svg-icons';
import { burnoutAPI } from '../services/api';
import '../pages/BurnoutWatch.css';

/**
 * Burnout Watch Widget - Displays quiet protection status on dashboard
 * Visual identity: Shield icon, rose outline, breathing pulse when active
 * No alarms, no red warnings - always calm
 */
const BurnoutWatchWidget = () => {
  const [watchStatus, setWatchStatus] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    fetchWatchStatus();
    // Update every minute in background
    const interval = setInterval(fetchWatchStatus, 60000);
    return () => clearInterval(interval);
  }, []);

  const fetchWatchStatus = async () => {
    try {
      console.log('🛡️ Burnout Watch: Fetching status...');
      const response = await burnoutAPI.getWatchStatus();
      console.log('🛡️ Burnout Watch: Status received:', response.data);
      setWatchStatus(response.data);
      setError(null);
    } catch (err) {
      console.error('🛡️ Burnout Watch: Error fetching status:', err);
      setError(err.response?.data?.message || err.message || 'Failed to load protection status');
    } finally {
      setLoading(false);
    }
  };

  // Loading state
  if (loading) {
    return (
      <div style={{
        backgroundColor: '#1a1a1a',
        border: '1px solid #333',
        borderRadius: '12px',
        padding: '1.5rem',
        minHeight: '150px',
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        justifyContent: 'center'
      }}>
        <div className="breathing-pulse">
          <FontAwesomeIcon icon={faShield} style={{ fontSize: '2rem', color: '#FFB6C1' }} />
        </div>
        <p style={{ color: '#999', marginTop: '1rem', fontSize: '0.9rem' }}>
          Loading protection status...
        </p>
      </div>
    );
  }

  // Error state
  if (error) {
    return (
      <div style={{
        backgroundColor: '#1a1a1a',
        border: '2px solid #fbbf24',
        borderRadius: '12px',
        padding: '1.5rem',
        minHeight: '150px'
      }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: '0.75rem', marginBottom: '1rem' }}>
          <FontAwesomeIcon icon={faExclamationTriangle} style={{ fontSize: '1.5rem', color: '#fbbf24' }} />
          <h3 style={{ margin: 0, fontSize: '1.1rem', color: '#fbbf24' }}>
            Connection Issue
          </h3>
        </div>
        <p style={{ color: '#999', fontSize: '0.9rem', marginBottom: '1rem' }}>
          {error}
        </p>
        <button 
          onClick={fetchWatchStatus}
          style={{
            padding: '0.5rem 1rem',
            background: '#FFB6C1',
            border: 'none',
            borderRadius: '4px',
            color: '#000',
            cursor: 'pointer',
            fontSize: '0.9rem',
            fontWeight: '600'
          }}
        >
          Retry Connection
        </button>
      </div>
    );
  }

  const getLevelColor = (level) => {
    switch (level) {
      case 'protecting':
      case 'watching':
        return '#FFB6C1'; // Rose
      default:
        return '#10b981'; // Green for healthy
    }
  };

  const shouldPulse = watchStatus?.protected === true || 
                      watchStatus?.level === 'protecting' || 
                      watchStatus?.level === 'watching';

  return (
    <Link
      to="/burnout-watch"
      style={{
        textDecoration: 'none',
        display: 'block'
      }}
    >
      <div
        style={{
          backgroundColor: '#1a1a1a',
          border: `2px solid ${getLevelColor(watchStatus?.level)}`,
          borderRadius: '12px',
          padding: '1.5rem',
          cursor: 'pointer',
          transition: 'all 0.3s ease',
          minHeight: '150px',
          userSelect: 'none'
        }}
        onMouseEnter={(e) => {
          e.currentTarget.style.transform = 'translateY(-2px)';
          e.currentTarget.style.boxShadow = '0 4px 12px rgba(255, 182, 193, 0.2)';
        }}
        onMouseLeave={(e) => {
          e.currentTarget.style.transform = 'translateY(0)';
          e.currentTarget.style.boxShadow = 'none';
        }}
      >
        {/* Header */}
        <div style={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          marginBottom: '1rem'
        }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: '0.75rem' }}>
            <div className={shouldPulse ? 'breathing-pulse' : ''}>
              <FontAwesomeIcon 
                icon={faShield} 
                style={{ 
                  fontSize: '1.5rem', 
                  color: '#FFB6C1'
                }} 
              />
            </div>
            <h3 style={{ 
              margin: 0, 
              fontSize: '1.1rem',
              color: '#FFB6C1'
            }}>
              Burnout Watch
            </h3>
          </div>
          
          {/* Status Badge */}
          <div style={{
            padding: '0.25rem 0.75rem',
            borderRadius: '12px',
            fontSize: '0.75rem',
            fontWeight: '600',
            backgroundColor: `${getLevelColor(watchStatus?.level)}20`,
            color: getLevelColor(watchStatus?.level),
            border: `1px solid ${getLevelColor(watchStatus?.level)}`
          }}>
            {watchStatus?.level?.toUpperCase() || 'CHECKING'}
          </div>
        </div>

        {/* Message */}
        <p style={{
          margin: 0,
          fontSize: '0.95rem',
          lineHeight: '1.5',
          color: getLevelColor(watchStatus?.level)
        }}>
          {watchStatus?.message || 'Loading protection status...'}
        </p>

        {/* Protection Indicators */}
        {(watchStatus?.soft_lock_active || watchStatus?.recovery_active || watchStatus?.tomorrow_lightened) && (
          <div style={{
            marginTop: '1rem',
            paddingTop: '1rem',
            borderTop: '1px solid #333',
            display: 'flex',
            flexWrap: 'wrap',
            gap: '0.5rem'
          }}>
            {watchStatus?.soft_lock_active && (
              <span style={{
                fontSize: '0.75rem',
                padding: '0.25rem 0.5rem',
                borderRadius: '8px',
                backgroundColor: 'rgba(255, 182, 193, 0.15)',
                border: '1px solid #FFB6C1',
                color: '#FFB6C1'
              }}>
                Soft Lock Active
              </span>
            )}
            {watchStatus?.recovery_active && (
              <span style={{
                fontSize: '0.75rem',
                padding: '0.25rem 0.5rem',
                borderRadius: '8px',
                backgroundColor: 'rgba(255, 182, 193, 0.15)',
                border: '1px solid #FFB6C1',
                color: '#FFB6C1'
              }}>
                Recovery Mode
              </span>
            )}
            {watchStatus?.tomorrow_lightened && (
              <span style={{
                fontSize: '0.75rem',
                padding: '0.25rem 0.5rem',
                borderRadius: '8px',
                backgroundColor: 'rgba(255, 182, 193, 0.15)',
                border: '1px solid #FFB6C1',
                color: '#FFB6C1'
              }}>
                Tomorrow Lightened
              </span>
            )}
          </div>
        )}

        {/* Subtle hint */}
        <p style={{
          margin: '1rem 0 0 0',
          fontSize: '0.8rem',
          color: '#666',
          textAlign: 'right'
        }}>
          Click to view details →
        </p>
      </div>
    </Link>
  );
};

export default BurnoutWatchWidget;
